import React, { useCallback } from 'react';
import useDragRotate from '../hooks/useDragRotate';
import CoinBack from './CoinBack';
import { sideFromAngle } from '../utils/coinSide';

/**
 * Terminal window header.
 * - Yellow button + double-click on header => minimize/expand
 * - Click on the logo => 3D physics-based coin spin
 * - Drag the logo horizontally => rotateY with momentum + spring
 */
const TerminalHeader = ({ logoSrc, isMinimized, onToggleMinimize }) => {
  const { angle, isDragging, isAnimating, handlers, wasDragged, flip } = useDragRotate({ sensitivity: 1.1 });

  const handleMinimizeClick = (e) => {
    e.stopPropagation();
    if (onToggleMinimize) onToggleMinimize();
  };

  const minimizeLabel = isMinimized ? 'Expandir terminal' : 'Minimizar terminal';

  const handleHeaderDoubleClick = (e) => {
    if (e.target.closest('button')) return;
    if (onToggleMinimize) onToggleMinimize();
  };

  const handleLogoClick = useCallback((e) => {
    e.stopPropagation();
    if (wasDragged()) return;
    flip(Math.random() > 0.5 ? 1 : -1, 0);
  }, [flip, wasDragged]);

  return (
    <div
      className="terminal-header"
      onDoubleClick={handleHeaderDoubleClick}
      title="Doble clic para minimizar/expandir"
      data-testid="terminal-header"
    >
      <button
        type="button"
        className="terminal-button close"
        aria-label="close"
        data-testid="terminal-btn-close"
        tabIndex={-1}
      />
      <button
        type="button"
        className={`terminal-button minimize${isMinimized ? ' is-minimized' : ''}`}
        aria-label={minimizeLabel}
        title={minimizeLabel}
        onClick={handleMinimizeClick}
        data-testid="terminal-btn-minimize"
      />
      <button
        type="button"
        className="terminal-button maximize"
        aria-label="maximize"
        data-testid="terminal-btn-maximize"
        tabIndex={-1}
      />
      <span className="terminal-title">c4sh3r@portfolio: ~</span>

      {logoSrc && (
        <button
          type="button"
          className={`terminal-header-badge coin-trigger coin-stage${isDragging ? ' is-dragging' : ''}`}
          onClick={handleLogoClick}
          aria-label="C4sh3r logo - click to flip, drag to rotate"
          title="Click para girar / arrastra"
          data-testid="terminal-header-logo-btn"
          {...handlers}
        >
          <span className="terminal-header-badge-dot" />
          <span
            className={`coin coin-sm${isAnimating ? ' is-spinning' : ''}${isDragging ? ' is-dragging' : ''}`}
            style={{ transform: `rotateY(${angle}deg)` }}
            data-side={sideFromAngle(angle)}
            aria-hidden="true"
          >
            <span className="coin-face coin-front">
              <img
                src={logoSrc}
                alt=""
                className="terminal-header-logo"
                data-testid="terminal-header-logo"
                draggable={false}
              />
            </span>
            <span className="coin-face coin-back">
              <CoinBack size="sm" />
            </span>
          </span>
        </button>
      )}
    </div>
  );
};

export default TerminalHeader;
